import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { TrendingHottestItem } from "../shared/type";
import { LocalizedLink } from "../i18n/LocaleRouting";
import { trendingItemPathFromItem } from "../utils/trendingItemId";

/** How long each front card dwells before auto-advancing. */
const AUTO_DWELL_MS = 1500;
/** Resume auto-spin after interaction ends. */
const AUTO_RESUME_MS = 2200;
/** px drag → degrees. */
const DRAG_SENSITIVITY = 0.35;
const SNAP_MS = 550;

function shortestAngleDelta(from: number, to: number): number {
  let delta = ((to - from) % 360) + 360;
  delta %= 360;
  if (delta > 180) delta -= 360;
  return delta;
}

function circularIndexDistance(
  index: number,
  activeIndex: number,
  length: number,
): number {
  if (length <= 0) return 0;
  let offset = Math.abs(index - activeIndex);
  return Math.min(offset, length - offset);
}

function ringRadiusPx(count: number, cardWidth: number): number {
  if (count <= 1) return 0;
  // Radius near the edge-touching fit; lower = tighter card spacing.
  const base = cardWidth / (2 * Math.tan(Math.PI / count));
  return Math.max(140, Math.min(260, base * 1.65));
}

const Trending3D: React.FC<{
  trendingData: TrendingHottestItem[];
  className?: string;
  isShow?: boolean;
}> = ({ trendingData, className, isShow }) => {
  const { t } = useTranslation();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const rotationRef = useRef(0);
  const draggingRef = useRef(false);
  const dragMovedRef = useRef(false);
  const pointerIdRef = useRef<number | null>(null);
  const dragStartXRef = useRef(0);
  const dragStartRotRef = useRef(0);
  const pauseAutoRef = useRef(false);
  const resumeTimerRef = useRef<number | null>(null);
  const autoTimerRef = useRef<number | null>(null);
  const reduceMotionRef = useRef(false);
  const snapToIndexRef = useRef<
    (index: number, withTransition?: boolean) => void
  >(() => {});

  const [isTitleVisible, setIsTitleVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [cardWidth, setCardWidth] = useState(150);

  const count = trendingData.length;
  const step = count > 0 ? 360 / count : 0;
  const radius = ringRadiusPx(count, cardWidth);

  const applyRotation = (deg: number, withTransition: boolean) => {
    rotationRef.current = deg;
    const el = ringRef.current;
    if (el) {
      el.style.transition = withTransition
        ? `transform ${SNAP_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
        : "none";
      el.style.transform = `rotateY(${deg}deg)`;
    }
    if (count > 0) {
      const idx = (((Math.round(-deg / step) % count) + count) %
        count) as number;
      setActiveIndex((prev) => (prev === idx ? prev : idx));
    }
  };

  const snapToIndex = (index: number, withTransition = true) => {
    if (count <= 0) return;
    const target = -index * step;
    const delta = shortestAngleDelta(rotationRef.current, target);
    applyRotation(rotationRef.current + delta, withTransition);
  };
  snapToIndexRef.current = snapToIndex;

  const clearAutoTimer = () => {
    if (autoTimerRef.current != null) {
      window.clearTimeout(autoTimerRef.current);
      autoTimerRef.current = null;
    }
  };

  const clearResumeTimer = () => {
    if (resumeTimerRef.current != null) {
      window.clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
  };

  const canAutoPlay = () =>
    !pauseAutoRef.current &&
    !draggingRef.current &&
    !reduceMotionRef.current &&
    count > 1;

  const startAutoLoop = () => {
    clearAutoTimer();
    if (!canAutoPlay()) return;
    // Dwell on the current front card, then advance one step.
    autoTimerRef.current = window.setTimeout(() => {
      if (!canAutoPlay()) return;
      const current =
        ((Math.round(-rotationRef.current / step) % count) + count) % count;
      snapToIndexRef.current((current + 1) % count, true);
      // After the snap animation finishes, begin the next dwell.
      autoTimerRef.current = window.setTimeout(() => {
        startAutoLoop();
      }, SNAP_MS);
    }, AUTO_DWELL_MS);
  };

  const pauseAuto = () => {
    pauseAutoRef.current = true;
    clearAutoTimer();
    clearResumeTimer();
  };

  const scheduleAutoResume = () => {
    if (reduceMotionRef.current) return;
    clearResumeTimer();
    resumeTimerRef.current = window.setTimeout(() => {
      pauseAutoRef.current = false;
      resumeTimerRef.current = null;
      startAutoLoop();
    }, AUTO_RESUME_MS);
  };

  useEffect(() => {
    if (!titleRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsTitleVisible(true);
          observer.unobserve(titleRef.current!);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const sync = () => setCardWidth(mq.matches ? 170 : 148);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => {
      reduceMotionRef.current = mq.matches;
      if (mq.matches) pauseAutoRef.current = true;
    };
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    rotationRef.current = 0;
    setActiveIndex(0);
  }, [trendingData]);

  // Keep ring transform in sync without React overwriting it each render.
  useLayoutEffect(() => {
    applyRotation(rotationRef.current, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendingData, count, step, radius, cardWidth]);

  useEffect(() => {
    pauseAutoRef.current = reduceMotionRef.current;
    clearResumeTimer();
    if (!reduceMotionRef.current) {
      startAutoLoop();
    }
    return () => {
      clearAutoTimer();
      clearResumeTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, step, trendingData]);

  // const goPrev = () => {
  //   pauseAuto();
  //   snapToIndex((activeIndex - 1 + count) % count);
  //   scheduleAutoResume();
  // };

  // const goNext = () => {
  //   pauseAuto();
  //   snapToIndex((activeIndex + 1) % count);
  //   scheduleAutoResume();
  // };

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    pauseAuto();
    draggingRef.current = true;
    dragMovedRef.current = false;
    pointerIdRef.current = e.pointerId;
    dragStartXRef.current = e.clientX;
    dragStartRotRef.current = rotationRef.current;
    stageRef.current?.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current || pointerIdRef.current !== e.pointerId) return;
    const dx = e.clientX - dragStartXRef.current;
    if (Math.abs(dx) > 6) dragMovedRef.current = true;
    applyRotation(dragStartRotRef.current + dx * DRAG_SENSITIVITY, false);
  };

  const endDrag = (e: React.PointerEvent) => {
    if (!draggingRef.current || pointerIdRef.current !== e.pointerId) return;
    draggingRef.current = false;
    pointerIdRef.current = null;
    try {
      stageRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    if (count > 0) {
      const nearest =
        ((Math.round(-rotationRef.current / step) % count) + count) % count;
      snapToIndex(nearest, true);
    }
    scheduleAutoResume();
  };

  const onCardActivate = (index: number) => {
    if (dragMovedRef.current) return;
    if (index === activeIndex) return;
    pauseAuto();
    snapToIndex(index, true);
    scheduleAutoResume();
  };

  if (!trendingData?.length) return null;

  const cardHeight = Math.round(cardWidth * 1.45);
  const halfW = cardWidth / 2;
  const halfH = cardHeight / 2;

  return (
    <div className={`text-[#181818] w-full ${className ?? ""}`}>
      <h1
        ref={titleRef}
        className={`flex ${
          isShow ? "justify-start text-xl " : "xl:text-3xl text-2xl "
        } items-center gap-x-1 fade-in-section ${
          isTitleVisible ? "fade-in-visible" : ""
        }`}
      >
        <span className="lg:hidden">🔥</span>
        <b>{t("home.trending")}</b>
      </h1>

      <div className="relative w-full max-w-[720px] mx-auto select-none px-8 sm:px-12 mt-0 overflow-hidden">
        <div
          ref={stageRef}
          className="relative h-[320px] sm:h-[380px] flex items-center justify-center touch-pan-y cursor-grab active:cursor-grabbing"
          style={{
            perspective: "1400px",
            perspectiveOrigin: "50% 45%",
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <div
            className="relative w-full h-full"
            style={{
              transformStyle: "preserve-3d",
              WebkitTransformStyle: "preserve-3d",
            }}
          >
            <div
              ref={ringRef}
              className="absolute inset-0"
              style={{
                transformStyle: "preserve-3d",
                WebkitTransformStyle: "preserve-3d",
                willChange: "transform",
              }}
            >
              {trendingData.map((item, index) => {
                const angle = index * step;
                const isActive = index === activeIndex;
                const dist = circularIndexDistance(index, activeIndex, count);
                // const opacity = isActive ? 1 : Math.max(0.38, 1 - dist * 0.14);
                // Center + immediate left/right stay solid; farther cards more faded.
                const opacity =
                  dist <= 1 ? 1 : Math.max(0.18, 0.42 - (dist - 2) * 0.12);

                const cardInner = (
                  <div
                    className="relative overflow-hidden rounded-2xl bg-[#111]/0 shadow-[0_14px_36px_rgba(0,0,0,0.55)]/0 ring-0 ring-white/10"
                    style={{
                      width: cardWidth,
                      height: cardHeight,
                      backfaceVisibility: "hidden",
                      WebkitBackfaceVisibility: "hidden",
                      transform: "translateZ(0)",
                    }}
                  >
                    <img
                      src={item.thumbnailUrl}
                      alt={item.name}
                      className="absolute inset-0 h-full w-full object-cover select-none [-webkit-user-drag:none] backdrop-saturate-150"
                      draggable={false}
                      onDragStart={(ev) => ev.preventDefault()}
                      loading={index < 4 ? "eager" : "lazy"}
                      decoding="async"
                    />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/0 via-black/0 to-transparent px-2.5 pb-2.5 pt-10">
                      <p className="text-[11px] sm:text-xs font-semibold text-white line-clamp-2 leading-snug drop-shadow">
                        {/* {item.name} */}
                      </p>
                    </div>
                  </div>
                );

                return (
                  <div
                    key={item.id}
                    className="absolute left-1/2 top-1/2"
                    style={{
                      width: cardWidth,
                      height: cardHeight,
                      marginLeft: -halfW,
                      marginTop: -halfH,
                      transform: `rotateY(${angle}deg) translateZ(${radius}px)`,
                      transformStyle: "preserve-3d",
                      WebkitTransformStyle: "preserve-3d",
                      zIndex: isActive ? 10 : 1,
                      opacity,
                    }}
                  >
                    {isActive ? (
                      <LocalizedLink
                        target="_top"
                        to={trendingItemPathFromItem(item)}
                        className="block"
                        draggable={false}
                        onClick={(ev) => {
                          if (dragMovedRef.current) {
                            ev.preventDefault();
                          }
                        }}
                      >
                        {cardInner}
                      </LocalizedLink>
                    ) : (
                      <button
                        type="button"
                        className="block appearance-none border-0 bg-transparent p-0 cursor-pointer"
                        onClick={() => onCardActivate(index)}
                        aria-label={`Show ${item.name}`}
                      >
                        {cardInner}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* <button
          type="button"
          onClick={goPrev}
          aria-label="Previous trending item"
          className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/80 text-[#444] flex items-center justify-center shadow-md hover:bg-white transition-colors"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button> */}
        {/* <button
          type="button"
          onClick={goNext}
          aria-label="Next trending item"
          className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/80 text-[#444] flex items-center justify-center shadow-md hover:bg-white transition-colors"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button> */}
      </div>
    </div>
  );
};

export default Trending3D;
